import { useEffect, useRef, useState } from 'react';
import { useBillingBanner } from '@/App';
import { Button } from '@/components/ui/button';
import { LogOut, Search, Loader2, ChevronDown, Menu, X, Home, CalendarDays, Users, UserCheck, Scissors, Package, DollarSign, BarChart2, Settings, ShieldCheck, Megaphone } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useNavigate, useLocation, NavLink } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { NotificationBell } from './NotificationBell';
import { OverdueAppointmentsBell } from './OverdueAppointmentsBell';
import { usePermissions } from '@/hooks/usePermissions';
import { useTenant } from '@/hooks/useTenant';
import { useOnboardingProgress } from '@/hooks/useOnboardingProgress';
import { matchesClientSearch, normalizeDigits, normalizeSearchText } from '@/lib/client-search';
import { buildMarketingNavItems, shouldShowMarketingNav, MARKETING_ACTIVE_HREFS, CLIENTS_HREF, CLIENTS_LABEL, MARKETING_LABEL } from '@/lib/navigation/marketingNav';

interface ClientResult {
  id: string;
  full_name: string;
  phone: string;
  email?: string | null;
}

// ── Global search ─────────────────────────────────────────────────────────────
function GlobalSearch() {
  const [query, setQuery]     = useState('');
  const [results, setResults] = useState<ClientResult[]>([]);
  const [clients, setClients] = useState<ClientResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen]       = useState(false);
  const containerRef          = useRef<HTMLDivElement>(null);
  const navigate              = useNavigate();
  const { user }              = useAuth();
  const { currentOrganization } = useTenant();

  useEffect(() => {
    if (!user?.organization_id) {
      setClients([]);
      return;
    }

    let cancelled = false;

    supabase
      .from('clients')
      .select('id, full_name, phone, email')
      .eq('organization_id', user.organization_id)
      .order('full_name', { ascending: true })
      .then(({ data, error }) => {
        if (cancelled || error) return;
        setClients((data ?? []) as ClientResult[]);
      });

    return () => {
      cancelled = true;
    };
  }, [user?.organization_id]);

  useEffect(() => {
    const normalizedTextQuery = normalizeSearchText(query);
    const normalizedDigitQuery = normalizeDigits(query);
    const canSearchByName = normalizedTextQuery.length >= 2;
    const canSearchByPhone = normalizedDigitQuery.length >= 4;

    if (!user?.organization_id || (!canSearchByName && !canSearchByPhone)) {
      setResults([]);
      setOpen(false);
      setLoading(false);
      return;
    }

    const timer = setTimeout(() => {
      setLoading(true);
      try {
        const nextResults = clients
          .filter((client) => matchesClientSearch(
            client,
            query,
            currentOrganization?.phone_prefix ?? '+56',
          ))
          .slice(0, 6);
        setResults(nextResults);
        setOpen(true);
      } finally { setLoading(false); }
    }, 200);
    return () => clearTimeout(timer);
  }, [clients, currentOrganization?.phone_prefix, query, user?.organization_id]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSelect = (client: ClientResult) => {
    const nextSearch = query.trim() || client.phone || client.full_name;
    navigate(`/clients?search=${encodeURIComponent(nextSearch)}`);
    setQuery(''); setOpen(false);
  };

  return (
    <div ref={containerRef} className="relative hidden lg:flex" style={{ width: 240 }}>
      <div
        className="flex items-center gap-2 w-full px-3"
        style={{
          height: 34, border: '1px solid rgba(255,255,255,0.25)', borderRadius: 8,
          background: 'rgba(255,255,255,0.12)', transition: 'border-color 0.15s',
        }}
      >
        {loading
          ? <Loader2 className="h-3.5 w-3.5 flex-shrink-0 animate-spin" style={{ color: 'rgba(255,255,255,0.7)' }} />
          : <Search  className="h-3.5 w-3.5 flex-shrink-0" style={{ color: 'rgba(255,255,255,0.7)' }} />
        }
        <input
          type="text"
          value={query}
          onChange={e => setQuery(e.target.value)}
          onKeyDown={e => e.key === 'Escape' && (setOpen(false), setQuery(''))}
          placeholder="Buscar cliente..."
          style={{
            flex: 1, border: 'none', outline: 'none', fontSize: 12,
            color: '#fff', background: 'transparent',
            fontFamily: "'Montserrat', sans-serif",
          }}
          onFocus={e => {
            (e.target.parentElement as HTMLElement).style.borderColor = 'rgba(255,255,255,0.6)';
            if (normalizeSearchText(query).length >= 2 || normalizeDigits(query).length >= 4) setOpen(true);
          }}
          onBlur={e => {
            (e.target.parentElement as HTMLElement).style.borderColor = 'rgba(255,255,255,0.25)';
          }}
        />
      </div>

      {open && (
        <div style={{
          position: 'absolute', top: 40, left: 0, right: 0, background: '#fff',
          border: '1px solid rgba(45,177,195,0.3)', borderRadius: 10,
          boxShadow: '0 4px 24px rgba(0,0,0,0.08)', zIndex: 9999, overflow: 'hidden',
        }}>
          {results.length === 0 ? (
            <div style={{ padding: '12px 14px', fontSize: 13, color: '#888', fontFamily: "'Montserrat', sans-serif" }}>
              Sin resultados para "{query}"
            </div>
          ) : results.map(client => (
            <button
              key={client.id}
              onMouseDown={() => handleSelect(client)}
              style={{
                width: '100%', textAlign: 'left', padding: '10px 14px',
                background: 'none', border: 'none', borderBottom: '1px solid #f3f4f6',
                cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: 2,
                fontFamily: "'Montserrat', sans-serif",
              }}
              onMouseEnter={e => (e.currentTarget.style.background = '#F0FAF8')}
              onMouseLeave={e => (e.currentTarget.style.background = 'none')}
            >
              <span style={{ fontWeight: 600, fontSize: 14, color: '#333' }}>{client.full_name}</span>
              <span style={{ fontSize: 12, color: '#888' }}>{client.phone}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Tenant avatar ─────────────────────────────────────────────────────────────
function TenantAvatar() {
  const { user } = useAuth();
  const [logoUrl, setLogoUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!user?.salon_id) return;
    supabase
      .from('salons')
      .select('logo_url')
      .eq('id', user.salon_id)
      .maybeSingle()
      .then(({ data }) => {
        if (data?.logo_url) setLogoUrl(data.logo_url);
      });
  }, [user?.salon_id]);

  if (logoUrl) {
    return <img src={logoUrl} alt="Logo" className="w-8 h-8 rounded-full object-cover border border-gray-200" />;
  }

  return (
    <div
      className="w-8 h-8 rounded-full flex items-center justify-center"
      style={{ background: 'linear-gradient(135deg, #58BDB3, #2db1c3)' }}
    >
      <span className="text-xs font-semibold text-white">
        {(user?.full_name || user?.email || '?')[0].toUpperCase()}
      </span>
    </div>
  );
}

// ── Nav item styles ───────────────────────────────────────────────────────────
const NAV_LINK_BASE: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 4,
  padding: '0 10px', height: 40, borderRadius: 6,
  fontSize: 13, fontWeight: 500, color: 'rgba(255,255,255,0.82)',
  textDecoration: 'none', whiteSpace: 'nowrap',
  transition: 'background 0.15s, color 0.15s',
  fontFamily: "'Montserrat', sans-serif",
  cursor: 'pointer', background: 'transparent', border: 'none',
};

// ── Horizontal nav ────────────────────────────────────────────────────────────
function HorizontalNav() {
  const { user } = useAuth();
  const { canAccessModule } = usePermissions();
  const { isLoading: tenantLoading, currentOrganization } = useTenant();
  const location = useLocation();
  const onboarding = useOnboardingProgress();
  const lockClientsMenu = onboarding.isActive && onboarding.currentStep?.id === 'create-client' && !onboarding.subItemStatus.hasClients;

  const industryType = currentOrganization?.industry_type ?? null;

  const can = (module: string) => {
    if (tenantLoading) return false;
    return canAccessModule(module);
  };

  const marketingItems = buildMarketingNavItems(
    { crm: can('crm'), loyalty: can('loyalty'), appointments: can('appointments') },
    industryType,
  );

  const isActive = (hrefs: string[]) =>
    hrefs.some(h => location.pathname.startsWith(h));

  const activeStyle: React.CSSProperties = {
    background: 'rgba(255,255,255,0.15)',
    color: '#fff',
  };

  const hoverStyle = (active: boolean): React.CSSProperties => ({
    ...NAV_LINK_BASE,
    ...(active ? activeStyle : {}),
  });

  // Dropdown item component
  const DropItem = ({ href, label, className, state }: { href: string; label: string; className?: string; state?: Record<string, unknown> }) => (
    <DropdownMenuItem asChild className={className}>
      <NavLink
        to={href}
        state={state}
        style={{ fontFamily: "'Montserrat', sans-serif", fontSize: 13, fontWeight: 500 }}
      >
        {label}
      </NavLink>
    </DropdownMenuItem>
  );

  // Simple nav link
  const NavItem = ({ href, label, dataOnboarding }: { href: string; label: string; dataOnboarding?: string }) => {
    const active = location.pathname.startsWith(href);
    return (
      <NavLink
        to={href}
        data-onboarding={dataOnboarding}
        style={{ ...NAV_LINK_BASE, ...(active ? activeStyle : {}) }}
        onMouseEnter={e => { if (!active) (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.10)'; }}
        onMouseLeave={e => { if (!active) (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
      >
        {label}
      </NavLink>
    );
  };

  // Nav group with dropdown
  function NavGroup({ label, hrefs, children, dataOnboarding }: { label: string; hrefs: string[]; children: React.ReactNode; dataOnboarding?: string }) {
    const active = isActive(hrefs);
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            data-onboarding={dataOnboarding}
            style={{ ...hoverStyle(active) }}
            onMouseEnter={e => { if (!active) (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.10)'; }}
            onMouseLeave={e => { if (!active) (e.currentTarget as HTMLElement).style.background = active ? 'rgba(255,255,255,0.15)' : 'transparent'; }}
          >
            {label}
            <ChevronDown className="h-3 w-3 opacity-70" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="min-w-[160px]">
          {children}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  return (
    <nav className="flex items-center gap-0.5 ml-2 overflow-x-auto scrollbar-none" style={{ scrollbarWidth: 'none' }}>
      {can('appointments') && (
        <NavItem href="/dashboard" label="Inicio" />
      )}
      {can('appointments') && (
        <NavItem href="/appointments" label="Agenda" dataOnboarding="nav-appointments" />
      )}
      {can('clients') && (
        <NavItem href={CLIENTS_HREF} label={CLIENTS_LABEL} dataOnboarding="nav-clients" />
      )}
      {shouldShowMarketingNav({ crm: can('crm'), loyalty: can('loyalty') }) && (
        <NavGroup
          label={MARKETING_LABEL}
          hrefs={[...MARKETING_ACTIVE_HREFS]}
        >
          {marketingItems.map((item) => (
            <DropItem
              key={item.id}
              href={item.href}
              label={item.label}
              state={item.state}
              className={lockClientsMenu ? 'opacity-40 pointer-events-none' : ''}
            />
          ))}
        </NavGroup>
      )}
      {can('professionals') && (
        <NavItem href="/professionals" label="Profesionales" dataOnboarding="nav-professionals" />
      )}
      {can('services') && (
        <NavItem href="/services" label="Servicios" dataOnboarding="nav-services" />
      )}
      {can('inventory') && (
        <NavItem href="/inventory" label="Inventario" />
      )}
      {can('finances') && (
        <NavItem href="/finances" label="Finanzas" />
      )}
      {can('reports') && (
        <NavItem href="/reports" label="Estadísticas" />
      )}
      {can('settings') && (
        <NavItem href="/settings" label="Configuración" dataOnboarding="nav-settings" />
      )}
      {user?.role === 'super_admin' && (
        <NavItem href="/super-admin" label="Super Admin" />
      )}
    </nav>
  );
}

// ── Mobile nav (hamburger + Sheet) ───────────────────────────────────────────
function MobileNav() {
  const [open, setOpen] = useState(false);
  const { user } = useAuth();
  const { canAccessModule } = usePermissions();
  const { isLoading: tenantLoading, currentOrganization } = useTenant();
  const location = useLocation();
  const onboarding = useOnboardingProgress();
  const lockClientsMenu = onboarding.isActive && onboarding.currentStep?.id === 'create-client' && !onboarding.subItemStatus.hasClients;
  const industryType = currentOrganization?.industry_type ?? null;

  const can = (module: string) => !tenantLoading && canAccessModule(module);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  type NavSection = {
    label: string;
    href?: string;
    icon: React.ReactNode;
    visible: boolean;
    disabled?: boolean;
    children?: { label: string; href: string; disabled?: boolean; state?: Record<string, unknown> }[];
  };

  const sections: NavSection[] = [
    { label: 'Inicio',         href: '/dashboard',    icon: <Home className="w-4 h-4" />,      visible: can('appointments') },
    { label: 'Agenda',         href: '/appointments', icon: <CalendarDays className="w-4 h-4" />, visible: can('appointments') },
    { label: CLIENTS_LABEL,    href: CLIENTS_HREF,    icon: <Users className="w-4 h-4" />,      visible: can('clients') },
    {
      label: MARKETING_LABEL, icon: <Megaphone className="w-4 h-4" />,
      visible: shouldShowMarketingNav({ crm: can('crm'), loyalty: can('loyalty') }),
      children: buildMarketingNavItems(
        { crm: can('crm'), loyalty: can('loyalty'), appointments: can('appointments') },
        industryType,
      ).map((item) => ({ label: item.label, href: item.href, state: item.state, disabled: lockClientsMenu })),
    },
    { label: 'Profesionales',  href: '/professionals', icon: <UserCheck className="w-4 h-4" />, visible: can('professionals') },
    { label: 'Servicios',      href: '/services',      icon: <Scissors className="w-4 h-4" />,  visible: can('services') },
    { label: 'Inventario',     href: '/inventory',     icon: <Package className="w-4 h-4" />,   visible: can('inventory') },
    { label: 'Finanzas',       href: '/finances',      icon: <DollarSign className="w-4 h-4" />,visible: can('finances') },
    { label: 'Estadísticas',   href: '/reports',       icon: <BarChart2 className="w-4 h-4" />, visible: can('reports') },
    { label: 'Configuración',  href: '/settings',      icon: <Settings className="w-4 h-4" />,  visible: can('settings') },
    { label: 'Super Admin',    href: '/super-admin',   icon: <ShieldCheck className="w-4 h-4" />, visible: user?.role === 'super_admin' },
  ];

  return (
    <>
      <Button
        variant="ghost"
        size="icon"
        className="lg:hidden text-white hover:bg-white/10 flex-shrink-0"
        onClick={() => setOpen(true)}
      >
        <Menu className="w-5 h-5" />
      </Button>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="left" className="w-72 p-0 flex flex-col">
          <SheetHeader className="px-5 pt-5 pb-3 border-b">
            <SheetTitle className="text-left text-base font-semibold text-[#1c7a8b]">Menú</SheetTitle>
          </SheetHeader>

          <nav className="flex-1 overflow-y-auto py-3 px-3 space-y-0.5">
            {sections.filter(s => s.visible).map(section => {
              if (section.children) {
                return (
                  <div key={section.label} className="py-1">
                    <div className="flex items-center gap-2.5 px-3 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                      {section.icon}
                      {section.label}
                    </div>
                    <div className="pl-3 space-y-0.5 mt-0.5">
                      {section.children.map(child => {
                        const active = location.pathname.startsWith(child.href);
                        return (
                          <NavLink
                            key={child.href}
                            to={child.href}
                            state={child.state}
                            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors
                              ${active ? 'bg-[#1c7a8b]/10 text-[#1c7a8b]' : 'text-slate-700 hover:bg-slate-100'}
                              ${child.disabled ? 'opacity-40 pointer-events-none' : ''}`}
                          >
                            {child.label}
                          </NavLink>
                        );
                      })}
                    </div>
                  </div>
                );
              }

              const active = location.pathname.startsWith(section.href!);
              return (
                <NavLink
                  key={section.href}
                  to={section.href!}
                  className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors
                    ${active ? 'bg-[#1c7a8b] text-white' : 'text-slate-700 hover:bg-slate-100'}`}
                >
                  {section.icon}
                  {section.label}
                </NavLink>
              );
            })}
          </nav>
        </SheetContent>
      </Sheet>
    </>
  );
}

// ── Header ────────────────────────────────────────────────────────────────────
export function Header() {
  const { user, signOut } = useAuth();
  const hasBanner = useBillingBanner();

  return (
    <header
      className={`sticky z-40 w-full ${hasBanner ? 'top-10' : 'top-0'}`}
      style={{ background: '#1c7a8b', borderBottom: '1px solid rgba(255,255,255,0.12)', fontFamily: "'Montserrat', sans-serif" }}
    >
      <div className="relative flex h-14 items-center px-4 lg:px-6 gap-2 min-w-0">

        {/* Logo */}
        <div className="flex-shrink-0">
          <img
            src="/MESTLY_HORIZONTAL.png"
            alt="Mestly"
            style={{ height: '26px', objectFit: 'contain', filter: 'brightness(0) invert(1) opacity(0.92)' }}
          />
        </div>

        {/* Mobile hamburger */}
        <MobileNav />

        {/* Desktop nav — hidden on mobile */}
        <div className="hidden lg:flex">
          <HorizontalNav />
        </div>

        {/* Spacer */}
        <div className="flex-1 min-w-0" />

        {/* Right: search (desktop only) + bell + user */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <GlobalSearch />
          <OverdueAppointmentsBell />
          <NotificationBell />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="rounded-full hover:bg-white/10">
                <TenantAvatar />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <div className="flex flex-col space-y-1">
                  <p className="text-sm font-medium">Mi Cuenta</p>
                  <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => signOut()} className="text-destructive cursor-pointer">
                <LogOut className="mr-2 h-4 w-4" />
                Cerrar Sesión
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

      </div>
    </header>
  );
}
